// ============================================================================
// LES PÉRIODES DES ÉCRANS — les bornes qu'un écran demande au serveur, et l'ancre qui les fait vivre.
//
// ⚠️ EXTRAIT DU JOURNAL (2026-08-17) quand le tableau de bord en est devenu le second consommateur.
// Recopier `bornesDe` aurait fait dériver les deux écrans à la première correction — et la correction
// qui compte est déjà arrivée une fois : voir `useAncreDuJour` ci-dessous.
//
// ⚠️ LES BORNES SONT CALCULÉES DANS LE NAVIGATEUR, en AAAAMMJJ, et c'est délibéré : `datetime_fiscal`
// est l'heure LOCALE du magasin, le serveur tourne en UTC dans un conteneur. Lui laisser deviner
// « aujourd'hui » ferait commencer la journée du commerçant à 2 h du matin en été.
// ============================================================================
import { useEffect, useState } from 'react';
import { currentLang } from './lang';
import { key, t, type Translatable } from './i18n';

export type PeriodeId =
  | 'today' | 'yesterday' | 'week' | '7d' | '14d' | '30d'
  | 'month' | 'lastMonth' | 'year' | 'lastYear' | 'all';

/**
 * `AAAAMMJJHHMMSS` → « 16/08 14:30 », par DÉCOUPAGE — jamais par `new Date()`, qui rejouerait le fuseau.
 * ⚠️ REMONTÉ DU JOURNAL (2026-08-18) : le tableau de bord lit le même horodatage, et le format fiscal
 * ne doit être connu que d'un seul endroit.
 */
export function horodatageFiscal(dt: string): string {
  if (!/^\d{14}$/.test(dt)) return dt || '—';
  // ⚠️ L'ANNÉE EST LÀ, sur deux chiffres, comme la maquette (`jDayL`) : sur « tout l'historique »,
  //    deux ventes du 12/08 de deux années différentes s'affichaient à l'identique — et rien à
  //    l'écran ne permettait de les distinguer.
  return `${dt.slice(6, 8)}/${dt.slice(4, 6)}/${dt.slice(2, 4)} ${dt.slice(8, 10)}:${dt.slice(10, 12)}`;
}

/**
 * `AAAAMMJJHHMMSS` → l'instant correspondant, pour CALCULER une durée (« il y a 4 min »).
 * ⚠️ CONSTRUIT EN HEURE LOCALE, champ par champ : `new Date('20260818143000')` n'est pas une forme
 * reconnue, et la variante ISO serait lue en UTC — la « dernière vente » sauterait de deux heures.
 * ⚠️ À N'UTILISER QUE POUR UNE DURÉE : pour AFFICHER l'horodatage, c'est `horodatageFiscal` — passer
 * par une `Date` pour ré-imprimer une chaîne qu'on possède déjà, c'est s'exposer au fuseau pour rien.
 */
export function instantFiscal(dt: string | null | undefined): Date | null {
  if (!dt || !/^\d{14}$/.test(dt)) return null;
  const n = (a: number, b: number) => Number(dt.slice(a, b));
  return new Date(n(0, 4), n(4, 6) - 1, n(6, 8), n(8, 10), n(10, 12), n(12, 14));
}

/** Le jour d'une date, au format que le serveur attend. */
export function jourYmd(d: Date): string {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Les bornes d'une période, relatives à une ancre.
 * ⚠️ `all` ne rend AUCUNE borne — c'est « tout l'historique », pas une plage. Un écran qui compare à
 * l'an dernier (le tableau de bord) ne peut donc pas l'offrir : il n'y a pas de « même période » à
 * opposer à un historique sans début.
 */
export function bornesDe(p: PeriodeId, now: Date): { from?: string; to?: string } {
  if (p === 'all') return {};
  if (p === 'today') return { from: jourYmd(now), to: jourYmd(now) };
  if (p === 'yesterday') {
    const h = new Date(now); h.setDate(h.getDate() - 1);
    return { from: jourYmd(h), to: jourYmd(h) };
  }
  if (p === 'week') {
    // ⚠️ SEMAINE CALENDAIRE, LUNDI D'ABORD — et c'est autre chose que « 7 derniers jours », que le
    // sélecteur propose à part : un mardi, « cette semaine » compte deux jours, pas sept. Les deux
    // existent parce qu'on ne pose pas la même question (« où en est ma semaine » ≠ « ma dernière
    // semaine glissante »). `getDay()` rend 0 le dimanche : il faut le ramener à 6.
    const d = new Date(now);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return { from: jourYmd(d), to: jourYmd(now) };
  }
  if (p === 'month') {
    const d = new Date(now); d.setDate(1);
    return { from: jourYmd(d), to: jourYmd(now) };
  }
  if (p === 'lastMonth') {
    // ⚠️ Le mois PLEIN et révolu : `setDate(0)` d'un 1er du mois donne le dernier jour du précédent,
    // ce qui évite d'avoir à connaître 30, 31, 28 ou 29.
    const fin = new Date(now); fin.setDate(0);
    const debut = new Date(fin); debut.setDate(1);
    return { from: jourYmd(debut), to: jourYmd(fin) };
  }
  if (p === 'year') {
    const d = new Date(now); d.setMonth(0, 1);
    return { from: jourYmd(d), to: jourYmd(now) };
  }
  if (p === 'lastYear') {
    const a = now.getFullYear() - 1;
    return { from: jourYmd(new Date(a, 0, 1)), to: jourYmd(new Date(a, 11, 31)) };
  }
  // Fenêtres glissantes : « 7 jours » = les 6 précédents + aujourd'hui.
  const days = p === '30d' ? 29 : p === '14d' ? 13 : 6;
  const d = new Date(now); d.setDate(d.getDate() - days);
  return { from: jourYmd(d), to: jourYmd(now) };
}

/**
 * `AAAAMMJJ` ↔ `AAAA-MM-JJ`.
 * ⚠️ DEUX FORMATS, ET C'EST ASSUMÉ : le serveur reçoit des bornes compactes (elles se comparent comme
 * des chaînes et se collent à `datetime_fiscal`), tandis qu'un `<input type="date">` ne lit et n'écrit
 * QUE l'ISO. La conversion vit ici, à la frontière, plutôt qu'imposée au sélecteur partagé — qui sert
 * d'autres apps n'ayant pas la contrainte fiscale du back-office.
 */
export const isoDe = (ymd: string): string => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
export const ymdDe = (iso: string): string => iso.replace(/-/g, '');

/**
 * LES NOMS DES PÉRIODES — des CLÉS, pas des textes.
 * ⚠️ Cette table est évaluée AU CHARGEMENT du module : y écrire `t()` figerait le français avant que
 * la langue du compte n'arrive. `key()` déclare sans traduire ; c'est `libellePeriode` qui résout, au
 * rendu. La lire directement affiche le français — et quatre écrans l'ont fait sous un « 0 en dur ».
 */
export const LIBELLE_PERIODE: Record<PeriodeId, Translatable> = {
  today: key('Aujourd’hui'),
  yesterday: key('Hier'),
  week: key('Cette semaine'),
  '7d': key('7 derniers jours'),
  '14d': key('14 derniers jours'),
  '30d': key('30 derniers jours'),
  month: key('Ce mois-ci'),
  lastMonth: key('Mois dernier'),
  year: key('Cette année'),
  lastYear: key('Année dernière'),
  all: key('Tout l’historique'),
};

/** Le nom d'une période, RÉSOLU dans la langue du compte — le seul endroit où la table se lit. */
export const libellePeriode = (p: PeriodeId): string => t(LIBELLE_PERIODE[p]);

/**
 * L'ancre du jour — une date qui BASCULE d'elle-même au changement de journée.
 *
 * ⚠️ ELLE EXISTE À CAUSE D'UN VRAI DÉGÂT. Les bornes étaient calculées une fois par choix de période :
 * un onglet laissé ouvert dans le magasin la nuit continuait d'interroger la journée de la VEILLE sous
 * le libellé « Aujourd'hui » — et recliquer le segment déjà actif ne corrigeait rien, la valeur étant
 * inchangée, donc sans invalidation. Le commerçant lisait le chiffre d'hier en croyant lire le sien.
 *
 * ⚠️ LE RÉVEIL EST POSÉ UNE SECONDE APRÈS MINUIT, jamais sur la seconde pile : à la seconde exacte, un
 * décalage d'horloge d'un millième peut faire retomber le calcul sur la veille.
 */
export function useAncreDuJour(): Date {
  const [ancre, setAncre] = useState(() => new Date());
  useEffect(() => {
    const minuit = new Date(ancre);
    minuit.setHours(24, 0, 1, 0);
    const timer = setTimeout(() => setAncre(new Date()), Math.max(1000, minuit.getTime() - Date.now()));
    return () => clearTimeout(timer);
  }, [ancre]);
  return ancre;
}

/**
 * Une horloge qui AVANCE, à la minute — pour les durées relatives affichées à l'écran.
 *
 * ⚠️ MÊME DÉGÂT QUE L'ANCRE DU JOUR, à une autre échelle : sans elle, « dernière vente il y a 4 min »
 * reste écrit tel quel une heure plus tard, parce que la durée a été calculée au chargement et que
 * rien ne la recalcule. L'écran affirmerait alors une fraîcheur qu'il n'a pas (§23.0).
 */
export function useHorlogeMinute(): Date {
  const [now, setMaintenant] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setMaintenant(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * `AAAAMM` → « Septembre 2026 », dans la langue du compte.
 *
 * ⚠️ EXTRAIT À LA RÈGLE DE 2 : l'écran des archives et le centre de notifications rendent le MÊME mois,
 * et l'écrire deux fois aurait fait diverger deux écrans qui nomment la même période.
 *
 * ⚠️ 🔴 DÉRIVÉ D'`Intl`, JAMAIS une table de douze mois écrite à la main — le dépôt l'a déjà payé cinq
 * fois : un calendrier recopié ne se traduit pas, et une migration par outil n'en fait passer que les
 * mots que son détecteur reconnaît.
 *
 * ⚠️ LU AU RENDU (`currentLang()`), jamais capturé dans une constante de module : la langue du compte
 * n'arrive qu'après le chargement.
 *
 * ⚠️ 🔴 LA CASSE EST LAISSÉE À L’APPELANT, et ce n’est pas un détail : `Intl` rend « septembre » en
 * minuscules, ce qui est la forme JUSTE au milieu d’une phrase (« a vendu jusqu’en septembre 2026 ») et
 * la forme fausse en tête de cellule. Capitaliser ici imposerait la seconde partout.
 */
export function moisLongLisible(aaaamm: string, lang: string = currentLang()): string {
  const mois = Number(aaaamm.slice(4, 6));
  const an = Number(aaaamm.slice(0, 4));
  // ⚠️ UNE ENTRÉE ILLISIBLE SE REND TELLE QUELLE : inventer un mois de repli ferait dire à l’écran
  //    « janvier » sur une donnée qu’il n’a pas comprise.
  if (!Number.isFinite(an) || !Number.isFinite(mois) || mois < 1 || mois > 12) return aaaamm;
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(an, mois - 1, 1)));
}